// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import styled from 'styled-components';
import moment from 'moment';
import { i18n, getCompanyCurrency } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import Table from 'shared/components/common/Table';
import * as Actions from 'main-app/store/Actions';
import DeleteInvoiceMutation from 'main-app/mutations/DeleteInvoice';
import AddUpdateInvoiceModal from 'main-app/components/AddUpdateInvoiceModal';
import RecordPaymentModal from 'main-app/components/RecordPaymentModal';
import StripePaymentLinkModal from 'main-app/components/StripePaymentLinkModal';
import InvoiceStatusPill from 'shared/components/common/Pill/InvoiceStatusPill';
import Button from 'shared/components/common/Button';
import Logo from 'main-app/components/PageHeaderLogo/img/logo.svg';
import EmailInvoiceMutation from 'main-app/mutations/EmailInvoice';
import {
  PrintWrapper,
  PrintOnly,
  NoPrint,
  PrintPage,
  PrintDocHeader,
  PrintDocHeaderTitle,
  PrintDocHeaderCompany,
  PrintInfoGrid,
  PrintInfoColumn,
  PrintInfoRow,
  PrintInfoLabel,
  PrintNotes,
  PrintItemTable,
  PrintDocFooter,
  PrintSignatureGrid,
  PrintSignatureColumn,
  PrintSignatureLine,
  PrintSignatureUnderline,
} from 'shared/components/common/PrintLayout';
import type { RouterHistory, Match } from 'react-router';

const Content = styled.div`
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #333;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;

  &:last-child {
    font-weight: 600;
    border-top: 1px solid #eee;
    padding-top: 10px;
    margin-top: 4px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 24px;
`;

type Props = {
  history: RouterHistory,
  match: Match,
};

type State = {
  editModalOpen: boolean,
  paymentModalOpen: boolean,
  paymentLinkModalOpen: boolean,
};

const formatCurrency = (value: ?number, currency: string = getCompanyCurrency()) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

class Invoice extends PureComponent<Props, State> {
  state = {
    editModalOpen: false,
    paymentModalOpen: false,
    paymentLinkModalOpen: false,
  };

  handlePrintInvoice = () => {
    window.print();
  };

  handleEmailInvoice = (invoiceId: string) => {
    EmailInvoiceMutation.commit({
      variables: { input: { invoiceId } },
    })
      .then(() => {
        Actions.alertNotification(i18n.t('Invoice email sent successfully.'), 'success');
      })
      .catch(() => {
        Actions.alertNotification(i18n.t('Error sending invoice email.'), 'error');
      });
  };

  handleDelete = (invoiceId: string) => {
    const { history } = this.props;
    DeleteInvoiceMutation.commit({
      variables: { input: { id: invoiceId } },
      onCompleted: () => {
        Actions.alertNotification(i18n.t('Invoice deleted.'), 'success');
        history.push('/invoices');
      },
      onError: () => {
        Actions.alertNotification(i18n.t('Error deleting invoice.'), 'error');
      },
    });
  };

  getLineColumns = () => [
    {
      Header: i18n.t('Description'),
      accessor: 'description',
      minWidth: 200,
      Cell: ({ original }: *) => original.description || (original.item && original.item.name) || '—',
    },
    {
      Header: i18n.t('Quantity'),
      accessor: 'quantity',
      width: 100,
    },
    {
      Header: i18n.t('Unit Price'),
      accessor: 'unitPrice',
      width: 120,
      Cell: ({ value }: *) => formatCurrency(value),
    },
    {
      Header: i18n.t('Discount %'),
      accessor: 'discountPercent',
      width: 100,
      Cell: ({ value }: *) => (value ? `${value}%` : '—'),
    },
    {
      Header: i18n.t('Line Total'),
      accessor: 'lineTotal',
      width: 120,
      Cell: ({ value }: *) => formatCurrency(value),
    },
  ];

  getPaymentColumns = () => [
    {
      Header: i18n.t('Date'),
      accessor: 'paymentDate',
      width: 120,
      Cell: ({ value }: *) => (value ? moment(value).format('MM/DD/YYYY') : '—'),
    },
    {
      Header: i18n.t('Amount'),
      accessor: 'amount',
      width: 120,
      Cell: ({ value }: *) => formatCurrency(value),
    },
    {
      Header: i18n.t('Method'),
      accessor: 'paymentMethod',
      width: 140,
      Cell: ({ value }: *) => (value || '').replace(/_/g, ' '),
    },
    {
      Header: i18n.t('Reference'),
      accessor: 'referenceNumber',
      minWidth: 160,
      Cell: ({ value }: *) => value || '—',
    },
    {
      Header: i18n.t('Notes'),
      accessor: 'notes',
      minWidth: 160,
      Cell: ({ value }: *) => value || '—',
    },
  ];

  render() {
    const { match, history } = this.props;
    const { editModalOpen, paymentModalOpen, paymentLinkModalOpen } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        cacheConfig={{ force: true }}
        query={graphql`
          query InvoiceQuery($id: ID) {
            invoice(id: $id) {
              id
              invoiceNumber
              status
              currency
              subtotal
              discountPercent
              discountAmount
              taxRate
              taxAmount
              totalAmount
              amountPaid
              amountDue
              partialPercent
              milestoneLabel
              billingAddressLine1
              billingAddressLine2
              billingAddressCity
              billingAddressState
              billingAddressZip
              issueDate
              dueDate
              paidDate
              notes
              terms
              order {
                id
                orderNumber
              }
              customer {
                id
                name
                email
              }
              lineItems {
                id
                description
                quantity
                unitPrice
                discountPercent
                lineTotal
                sortOrder
                item {
                  id
                  name
                }
              }
              payments {
                id
                amount
                paymentMethod
                referenceNumber
                paymentDate
                notes
                createdAt
              }
              updatedAt
              createdAt
            }
            viewer {
              id
              permissions
              company {
                id
                name
                addressLine1
                addressLine2
                addressCity
                addressState
                addressZip
                addressCountry
                phone
              }
            }
          }
        `}
        variables={{ id: match.params.id }}
        render={({ props }) => {
          if (!props) return <Page><Loader /></Page>;

          const { invoice, viewer } = props;
          const canEdit = viewer.permissions.includes('UPDATE_INVOICE');
          const canDelete = viewer.permissions.includes('DELETE_INVOICE');
          const canPay = viewer.permissions.includes('CREATE_PAYMENT');

          const contextMenuItems = [];
          if (canEdit) {
            contextMenuItems.push({
              label: i18n.t('Edit Invoice'),
              onClick: () => this.setState({ editModalOpen: true }),
            });
          }
          if (canDelete) {
            contextMenuItems.push({
              label: i18n.t('Delete Invoice'),
              onClick: () => this.handleDelete(invoice.id),
              destructive: true,
            });
          }

          return (
            <Page>
              <PageHeader
                title={`${i18n.t('Invoice')} ${invoice.invoiceNumber}`}
                backLabel={i18n.t('Invoices')}
                backTo="/invoices"
                contextMenuItems={contextMenuItems}
              />
              <PrintWrapper>
              <PrintOnly>
                <PrintPage>
                  <PrintDocHeader>
                    <PrintDocHeaderTitle>
                      <Logo style={{ width: 100, height: 'auto', display: 'block', marginBottom: 4 }} />
                      {i18n.t('Invoice')} {invoice.invoiceNumber}
                    </PrintDocHeaderTitle>
                    <PrintDocHeaderCompany>{viewer && viewer.company && viewer.company.name}</PrintDocHeaderCompany>
                  </PrintDocHeader>

                  <PrintInfoGrid>
                    <PrintInfoColumn>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Bill To')}</PrintInfoLabel>
                        <div>
                          <div>{invoice.customer.name}</div>
                          {(invoice.billingAddressLine1 || invoice.billingAddressCity) && (
                            <div style={{ marginTop: 2 }}>
                              {[invoice.billingAddressLine1, invoice.billingAddressLine2].filter(Boolean).map((line, i) => <div key={i}>{line}</div>)}
                              {(invoice.billingAddressCity || invoice.billingAddressState || invoice.billingAddressZip) && (
                                <div>{[invoice.billingAddressCity, invoice.billingAddressState, invoice.billingAddressZip].filter(Boolean).join(', ')}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </PrintInfoRow>
                      {invoice.order && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Order #')}</PrintInfoLabel>
                          <div>{invoice.order.orderNumber}</div>
                        </PrintInfoRow>
                      )}
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Status')}</PrintInfoLabel>
                        <div>{(invoice.status || '').replace(/_/g, ' ')}</div>
                      </PrintInfoRow>
                    </PrintInfoColumn>
                    <PrintInfoColumn>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Invoice Date')}</PrintInfoLabel>
                        <div>{invoice.issueDate ? moment(invoice.issueDate).format('MM/DD/YYYY') : '—'}</div>
                      </PrintInfoRow>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Due Date')}</PrintInfoLabel>
                        <div>{invoice.dueDate ? moment(invoice.dueDate).format('MM/DD/YYYY') : '—'}</div>
                      </PrintInfoRow>
                      {viewer && viewer.company && (
                        <>
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('From')}</PrintInfoLabel>
                            <div>{viewer.company.name}</div>
                          </PrintInfoRow>
                          {(viewer.company.addressLine1 || viewer.company.addressCity) && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Address')}</PrintInfoLabel>
                              <div>
                                {viewer.company.addressLine1 && <div>{viewer.company.addressLine1}</div>}
                                {viewer.company.addressLine2 && <div>{viewer.company.addressLine2}</div>}
                                {(viewer.company.addressCity || viewer.company.addressState || viewer.company.addressZip) && (
                                  <div>{[viewer.company.addressCity, viewer.company.addressState, viewer.company.addressZip].filter(Boolean).join(', ')}</div>
                                )}
                                {viewer.company.addressCountry && <div>{viewer.company.addressCountry}</div>}
                              </div>
                            </PrintInfoRow>
                          )}
                          {viewer.company.phone && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Phone')}</PrintInfoLabel>
                              <div>{viewer.company.phone}</div>
                            </PrintInfoRow>
                          )}
                        </>
                      )}
                    </PrintInfoColumn>
                  </PrintInfoGrid>

                  <PrintItemTable>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Description')}</th>
                        <th>{i18n.t('Qty')}</th>
                        <th style={{ textAlign: 'right' }}>{i18n.t('Unit Price')}</th>
                        <th style={{ textAlign: 'right' }}>{i18n.t('Discount')}</th>
                        <th style={{ textAlign: 'right' }}>{i18n.t('Line Total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(invoice.lineItems || []).map(line => (
                        <tr key={line.id}>
                          <td>{line.description || (line.item && line.item.name) || '—'}</td>
                          <td style={{ textAlign: 'center' }}>{line.quantity}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(line.unitPrice, invoice.currency)}</td>
                          <td style={{ textAlign: 'right' }}>{line.discountPercent ? `${line.discountPercent}%` : '—'}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(line.lineTotal, invoice.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </PrintItemTable>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '16px 0 24px' }}>
                    <table style={{ borderSpacing: 0, minWidth: 280 }}>
                      <tbody>
                        <tr>
                          <td style={{ padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Subtotal:')}</td>
                          <td style={{ padding: '4px 0', textAlign: 'right' }}>{formatCurrency(invoice.subtotal, invoice.currency)}</td>
                        </tr>
                        {invoice.discountPercent > 0 && (
                          <tr>
                            <td style={{ padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Discount')} ({invoice.discountPercent}%):</td>
                            <td style={{ padding: '4px 0', textAlign: 'right' }}>-{formatCurrency(invoice.discountAmount, invoice.currency)}</td>
                          </tr>
                        )}
                        {invoice.taxRate > 0 && (
                          <tr>
                            <td style={{ padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Tax')} ({invoice.taxRate}%):</td>
                            <td style={{ padding: '4px 0', textAlign: 'right' }}>{formatCurrency(invoice.taxAmount, invoice.currency)}</td>
                          </tr>
                        )}
                        <tr>
                          <td style={{ fontWeight: 700, padding: '8px 16px 8px 0', textAlign: 'right', borderTop: '2px solid #333' }}>{i18n.t('Total:')}</td>
                          <td style={{ padding: '8px 0', textAlign: 'right', borderTop: '2px solid #333', fontWeight: 700 }}>{formatCurrency(invoice.totalAmount, invoice.currency)}</td>
                        </tr>
                        <tr>
                          <td style={{ padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Amount Paid:')}</td>
                          <td style={{ padding: '4px 0', textAlign: 'right' }}>{formatCurrency(invoice.amountPaid, invoice.currency)}</td>
                        </tr>
                        <tr>
                          <td style={{ fontWeight: 700, padding: '4px 16px 4px 0', textAlign: 'right' }}>{i18n.t('Amount Due:')}</td>
                          <td style={{ padding: '4px 0', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(invoice.amountDue, invoice.currency)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {invoice.payments && invoice.payments.length > 0 && (
                    <div style={{ marginBottom: 24 }}>
                      <PrintInfoLabel style={{ display: 'block', marginBottom: 8 }}>{i18n.t('Payments')}</PrintInfoLabel>
                      <PrintItemTable>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left' }}>{i18n.t('Date')}</th>
                            <th style={{ textAlign: 'right' }}>{i18n.t('Amount')}</th>
                            <th style={{ textAlign: 'left' }}>{i18n.t('Method')}</th>
                            <th style={{ textAlign: 'left' }}>{i18n.t('Reference')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.payments.map(p => (
                            <tr key={p.id}>
                              <td>{p.paymentDate ? moment(p.paymentDate).format('MM/DD/YYYY') : '—'}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(p.amount, invoice.currency)}</td>
                              <td>{(p.paymentMethod || '').replace(/_/g, ' ')}</td>
                              <td>{p.referenceNumber || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </PrintItemTable>
                    </div>
                  )}

                  {(invoice.notes || invoice.terms) && (
                    <PrintNotes>
                      {invoice.terms && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Terms')}</PrintInfoLabel>
                          <div>{invoice.terms}</div>
                        </PrintInfoRow>
                      )}
                      {invoice.notes && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Notes')}</PrintInfoLabel>
                          <div>{invoice.notes}</div>
                        </PrintInfoRow>
                      )}
                    </PrintNotes>
                  )}

                  <PrintDocFooter>
                    <span>{i18n.t('Created')} {moment(invoice.createdAt).format('MM/DD/YYYY')}</span>
                  </PrintDocFooter>
                </PrintPage>
              </PrintOnly>
              <NoPrint>
              <Content>
                <ActionBar>
                  <Button
                    theme="grey"
                    size="small"
                    width="auto"
                    onClick={this.handlePrintInvoice}
                  >
                    {i18n.t('Print Invoice')}
                  </Button>
                  <Button
                    theme="blue"
                    size="small"
                    width="auto"
                    onClick={() => this.handleEmailInvoice(invoice.id)}
                  >
                    {i18n.t('Email Invoice')}
                  </Button>
                  {invoice.status !== 'PAID' && invoice.status !== 'VOID' && (
                    <Button
                      theme="green"
                      size="small"
                      width="auto"
                      onClick={() => this.setState({ paymentLinkModalOpen: true })}
                    >
                      {i18n.t('Create Payment Link')}
                    </Button>
                  )}
                </ActionBar>
                <Section>
                  <SectionTitle>{i18n.t('General')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Status')}</DetailLabel>
                      <DetailValue>
                        <InvoiceStatusPill status={invoice.status} />
                      </DetailValue>
                    </DetailItem>
                    {invoice.order && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Order')}</DetailLabel>
                        <DetailValue
                          style={{ cursor: 'pointer', color: '#1890ff' }}
                          onClick={() => history.push(`/order/${invoice.order.id}`)}
                        >
                          {invoice.order.orderNumber}
                        </DetailValue>
                      </DetailItem>
                    )}
                    <DetailItem>
                      <DetailLabel>{i18n.t('Customer')}</DetailLabel>
                      <DetailValue
                        style={{ cursor: 'pointer', color: '#1890ff' }}
                        onClick={() => history.push(`/customer/${invoice.customer.id}`)}
                      >
                        {invoice.customer.name}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Currency')}</DetailLabel>
                      <DetailValue>{invoice.currency || getCompanyCurrency()}</DetailValue>
                    </DetailItem>
                    {invoice.partialPercent != null && invoice.partialPercent < 100 && (
                      <DetailItem>
                        <DetailLabel>{i18n.t('Milestone Invoice')}</DetailLabel>
                        <DetailValue>
                          <span style={{
                            background: '#e6f7ff',
                            border: '1px solid #91d5ff',
                            borderRadius: 4,
                            padding: '2px 8px',
                            fontSize: 13,
                            color: '#1890ff',
                            fontWeight: 600,
                          }}>
                            {invoice.milestoneLabel || `${invoice.partialPercent}% Milestone`}
                          </span>
                        </DetailValue>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Dates')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Issue Date')}</DetailLabel>
                      <DetailValue>
                        {invoice.issueDate
                          ? moment(invoice.issueDate).format('MM/DD/YYYY')
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Due Date')}</DetailLabel>
                      <DetailValue>
                        {invoice.dueDate
                          ? moment(invoice.dueDate).format('MM/DD/YYYY')
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Paid Date')}</DetailLabel>
                      <DetailValue>
                        {invoice.paidDate
                          ? moment(invoice.paidDate).format('MM/DD/YYYY')
                          : '—'}
                      </DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Billing Address')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Address')}</DetailLabel>
                      <DetailValue>
                        {[
                          invoice.billingAddressLine1,
                          invoice.billingAddressLine2,
                        ]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('City')}</DetailLabel>
                      <DetailValue>{invoice.billingAddressCity || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('State')}</DetailLabel>
                      <DetailValue>{invoice.billingAddressState || '—'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Postal Code')}</DetailLabel>
                      <DetailValue>{invoice.billingAddressZip || '—'}</DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Line Items')}</SectionTitle>
                  {invoice.lineItems && invoice.lineItems.length > 0 ? (
                    <Table
                      data={invoice.lineItems}
                      columns={this.getLineColumns()}
                    />
                  ) : (
                    <DetailValue>{i18n.t('No line items.')}</DetailValue>
                  )}
                </Section>

                <Section>
                  <SectionTitle>{i18n.t('Summary')}</SectionTitle>
                  <div style={{ maxWidth: 300 }}>
                    {invoice.partialPercent != null && invoice.partialPercent < 100 && (() => {
                      const fullTotal = invoice.lineItems
                        ? invoice.lineItems.reduce((s, l) => s + (l.lineTotal || 0), 0)
                        : null;
                      return (
                        <>
                          {fullTotal != null && (
                            <SummaryRow>
                              <span style={{ color: '#888' }}>{i18n.t('Full Contract Value')}</span>
                              <span style={{ color: '#888' }}>{formatCurrency(fullTotal, invoice.currency)}</span>
                            </SummaryRow>
                          )}
                          <SummaryRow>
                            <span style={{ color: '#1890ff', fontWeight: 600 }}>
                              {invoice.milestoneLabel || `${invoice.partialPercent}% Milestone`}
                            </span>
                            <span style={{ color: '#1890ff', fontWeight: 600 }}>
                              {formatCurrency(invoice.totalAmount, invoice.currency)}
                            </span>
                          </SummaryRow>
                        </>
                      );
                    })()}
                    <SummaryRow>
                      <span>{i18n.t('Subtotal')}</span>
                      <span>{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                    </SummaryRow>
                    {invoice.discountPercent > 0 && (
                      <SummaryRow>
                        <span>{i18n.t('Discount')} ({invoice.discountPercent}%)</span>
                        <span>-{formatCurrency(invoice.discountAmount, invoice.currency)}</span>
                      </SummaryRow>
                    )}
                    {invoice.taxRate > 0 && (
                      <SummaryRow>
                        <span>{i18n.t('Tax')} ({invoice.taxRate}%)</span>
                        <span>{formatCurrency(invoice.taxAmount, invoice.currency)}</span>
                      </SummaryRow>
                    )}
                    <SummaryRow>
                      <span>{i18n.t('Total')}</span>
                      <span>{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
                    </SummaryRow>
                    <SummaryRow>
                      <span>{i18n.t('Amount Paid')}</span>
                      <span>{formatCurrency(invoice.amountPaid, invoice.currency)}</span>
                    </SummaryRow>
                    <SummaryRow>
                      <span>{i18n.t('Amount Due')}</span>
                      <span>{formatCurrency(invoice.amountDue, invoice.currency)}</span>
                    </SummaryRow>
                  </div>
                </Section>

                <Section>
                  <SectionHeader>
                    <SectionTitle>{i18n.t('Payments')}</SectionTitle>
                    {canPay && invoice.status !== 'PAID' && invoice.status !== 'VOID' && (
                      <Button
                        theme="blue"
                        size="small"
                        width="auto"
                        onClick={() => this.setState({ paymentModalOpen: true })}
                      >
                        {i18n.t('Record Payment')}
                      </Button>
                    )}
                  </SectionHeader>
                  {invoice.payments && invoice.payments.length > 0 ? (
                    <Table
                      data={invoice.payments}
                      columns={this.getPaymentColumns()}
                    />
                  ) : (
                    <DetailValue>{i18n.t('No payments recorded.')}</DetailValue>
                  )}
                </Section>

                {(invoice.notes || invoice.terms) && (
                  <Section>
                    <SectionTitle>{i18n.t('Notes & Terms')}</SectionTitle>
                    <DetailGrid>
                      {invoice.notes && (
                        <DetailItem>
                          <DetailLabel>{i18n.t('Notes')}</DetailLabel>
                          <DetailValue>{invoice.notes}</DetailValue>
                        </DetailItem>
                      )}
                      {invoice.terms && (
                        <DetailItem>
                          <DetailLabel>{i18n.t('Terms')}</DetailLabel>
                          <DetailValue>{invoice.terms}</DetailValue>
                        </DetailItem>
                      )}
                    </DetailGrid>
                  </Section>
                )}

                <Section>
                  <SectionTitle>{i18n.t('Timestamps')}</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Created')}</DetailLabel>
                      <DetailValue>
                        {moment(invoice.createdAt).format('MM/DD/YYYY hh:mm A')}
                      </DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>{i18n.t('Last Updated')}</DetailLabel>
                      <DetailValue>
                        {moment(invoice.updatedAt).format('MM/DD/YYYY hh:mm A')}
                      </DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </Section>
              </Content>
              </NoPrint>
              </PrintWrapper>
              {editModalOpen && (
                <AddUpdateInvoiceModal
                  onClose={() => this.setState({ editModalOpen: false })}
                  invoice={invoice}
                />
              )}
              {paymentModalOpen && (
                <RecordPaymentModal
                  onClose={() => this.setState({ paymentModalOpen: false })}
                  invoiceId={invoice.id}
                />
              )}
              {paymentLinkModalOpen && (
                <StripePaymentLinkModal
                  onClose={() => this.setState({ paymentLinkModalOpen: false })}
                  invoiceId={invoice.id}
                  customerEmail={invoice.customer.email}
                />
              )}
            </Page>
          );
        }}
      />
    );
  }
}

export default Invoice;
